import React, { useState } from "react";
import "./CreateQuestionnaire.css";
import publicAxios from "../../utils/axios/publicAxios";

const CreateQuestionnaire = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Add new section
  const handleAddSection = () => {
    const newSection = {
      id: Date.now(),
      title: "",
      questions: [],
    };
    setSections([...sections, newSection]);
  };

  // Update section title
  const handleSectionTitleChange = (id, value) => {
    setSections((prev) =>
      prev.map((sec) => (sec.id === id ? { ...sec, title: value } : sec))
    );
  };

  // Add question inside a section
  const handleAddQuestion = (sectionId) => {
    const newQuestion = {
      id: Date.now(),
      text: "",
    };
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? { ...sec, questions: [...sec.questions, newQuestion] }
          : sec
      )
    );
  };

  // Update question text
  const handleQuestionChange = (sectionId, questionId, value) => {
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              questions: sec.questions.map((q) =>
                q.id === questionId ? { ...q, text: value } : q
              ),
            }
          : sec
      )
    );
  };

  // Remove a section
  const handleRemoveSection = (sectionId) => {
    setSections((prev) => prev.filter((sec) => sec.id !== sectionId));
  };

  // Remove question
  const handleRemoveQuestion = (sectionId, questionId) => {
    setSections((prev) =>
      prev.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              questions: sec.questions.filter((q) => q.id !== questionId),
            }
          : sec
      )
    );
  };

  // Save questionnaire to backend
  const handleSave = async () => {
    if (!title.trim()) {
      setMessage("Please enter a title before saving.");
      return;
    }

    const questionnaire = {
      title,
      description,
      sections: sections.map((s) => ({
        title: s.title,
        questions: s.questions.map((q) => q.text),
      })),
    };

    try {
      setLoading(true);
      setMessage("");

      const response = await publicAxios.post("/public/create-questionnaire", questionnaire);

      if (response.status === 201 || response.status === 200) {
        setMessage("✅ Questionnaire saved successfully!");
        setTitle("");
        setDescription("");
        setSections([]);
      } else {
        setMessage("⚠️ Failed to save questionnaire. Please try again.");
      }
    } catch (error) {
      console.error("Save error:", error);
      setMessage("❌ Error saving questionnaire. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-questionnaire-container">
      <div className="questionnaire-header">
        <h1>Create Questionnaire</h1>
        <p className="subtitle">
          Build and organize professional questionnaires for your clients.
        </p>
      </div>

      <div className="questionnaire-main-info">
        <label>Main Title</label>
        <input
          type="text"
          placeholder="Enter questionnaire title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label>Description</label>
        <textarea
          placeholder="Describe the purpose of this questionnaire..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        ></textarea>
      </div>

      {/* Sections */}
      <div className="sections-container">
        {sections.map((section, i) => (
          <div key={section.id} className="section-card">
            <div className="section-header">
              <input
                type="text"
                placeholder={`Section ${i + 1} Title`}
                value={section.title}
                onChange={(e) =>
                  handleSectionTitleChange(section.id, e.target.value)
                }
              />
              <button
                className="remove-section-btn"
                onClick={() => handleRemoveSection(section.id)}
              >
                ✕
              </button>
            </div>

            <div className="questions-list">
              {section.questions.map((question, index) => (
                <div key={question.id} className="question-item">
                  <input
                    type="text"
                    placeholder={`Question ${index + 1}`}
                    value={question.text}
                    onChange={(e) =>
                      handleQuestionChange(
                        section.id,
                        question.id,
                        e.target.value
                      )
                    }
                  />
                  <button
                    className="remove-question-btn"
                    onClick={() =>
                      handleRemoveQuestion(section.id, question.id)
                    }
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <button
              className="add-question-btn"
              onClick={() => handleAddQuestion(section.id)}
            >
              + Add Question
            </button>
          </div>
        ))}
      </div>

      {/* Add Section */}
      <div className="add-section-container">
        <button className="add-section-btn" onClick={handleAddSection}>
          + Add Section
        </button>
      </div>

      {/* Save */}
      <div className="save-container">
        <button className="save-btn" onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "💾 Save Questionnaire"}
        </button>
        {message && <p className="status-message">{message}</p>}
      </div>
    </div>
  );
};

export default CreateQuestionnaire;